/**
 * Core Brand Palette
 * These are the foundational colors used across all features
 */
export const BRAND_PALETTE = {
  // Primary brand colors (already in brand-colors.css)
  primary: 'rgb(46, 79, 95)', // Deep teal-blue
  secondary: 'rgb(120, 144, 156)', // Muted blue-grey
  accent: 'rgb(255, 171, 64)', // Warm orange
  dark: 'rgb(33, 38, 41)', // Dark charcoal

  // Main colors (new specification)
  offBlack: 'rgb(33, 38, 41)', // Off Black
  sun: 'rgb(255, 171, 64)', // Sun color
  awake: 'rgb(54, 19, 0)', // Awake color
  white: 'rgb(255, 255, 255)', // White
  black: 'rgb(0, 0, 0)', // Black

  // Alpha variants
  primaryLight: 'rgba(46, 79, 95, 0.8)',
  primaryDark: 'rgba(46, 79, 95, 1.2)',
  secondaryLight: 'rgba(120, 144, 156, 0.6)',
  accentLight: 'rgba(255, 171, 64, 0.8)',
  accentSuperLight: 'rgba(255, 171, 64, 0.2)',
  secondaryAlpha10: 'rgba(120, 144, 156, 0.1)',
  secondaryAlpha30: 'rgba(120, 144, 156, 0.3)',
  accentAlpha10: 'rgba(255, 171, 64, 0.1)',
  accentAlpha30: 'rgba(255, 171, 64, 0.3)',

  // Gradient backgrounds
  heroGradient: 'linear-gradient(135deg, rgba(46, 79, 95, 0.4) 0%, rgba(33, 38, 41, 0.5) 100%)',
  aboutGradient: 'linear-gradient(135deg, rgba(46, 79, 95, 0.8) 0%, rgba(33, 38, 41, 0.9) 100%)',
  businessImpactBg: 'rgba(46, 79, 95, 0.8)',

  // White variants
  transparent: 'transparent'
} as const

/**
 * Typography Palette
 * Font specifications using Lexend Deca
 */
export const TYPOGRAPHY_PALETTE = {
  // Font family
  fontFamily: 'Lexend Deca, sans-serif',

  // Headlines - Lexend Deca Medium Bold
  headlineLarge: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '600', // Medium Bold
    fontSize: '2.5rem', // 40px
    lineHeight: '1.2',
    letterSpacing: '-0.025em'
  },
  headlineMedium: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '600', // Medium Bold
    fontSize: '2rem', // 32px
    lineHeight: '1.25',
    letterSpacing: '-0.025em'
  },
  headlineSmall: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '600', // Medium Bold
    fontSize: '1.5rem', // 24px
    lineHeight: '1.3',
    letterSpacing: '-0.025em'
  },

  // Body text - Lexend Deca normal or thin
  bodyLarge: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '400', // Normal
    fontSize: '1.125rem', // 18px
    lineHeight: '1.6',
    letterSpacing: '0.01em'
  },
  bodyMedium: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '400', // Normal
    fontSize: '1rem', // 16px
    lineHeight: '1.6',
    letterSpacing: '0.01em'
  },
  bodySmall: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '300', // Thin
    fontSize: '0.875rem', // 14px
    lineHeight: '1.5',
    letterSpacing: '0.02em'
  },

  // Caption text
  caption: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '300', // Thin
    fontSize: '0.75rem', // 12px
    lineHeight: '1.4',
    letterSpacing: '0.03em'
  },

  // Button text
  button: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '500', // Medium
    fontSize: '0.875rem', // 14px
    lineHeight: '1.4',
    letterSpacing: '0.02em'
  },

  // Label text
  label: {
    fontFamily: 'Lexend Deca, sans-serif',
    fontWeight: '500', // Medium
    fontSize: '0.75rem', // 12px
    lineHeight: '1.4',
    letterSpacing: '0.05em'
  }
} as const

/**
 * Status & Semantic Colors
 * Used for success, error, warning, and info states
 */
export const STATUS_COLORS = {
  success: '#10b981', // green-500
  warning: '#f59e0b', // amber-500
  error: '#ef4444', // red-500
  info: '#3b82f6', // blue-500

  // Chart colors
  chartGreen: '#10b981',
  chartBlue: '#3b82f6',
  chartAmber: '#f59e0b',
  chartOrange: '#f97316',
  chartRed: '#ef4444',
  chartPurple: '#8b5cf6'
} as const

/**
 * Chart & Data Visualization Colors
 * Specific colors for charts and metrics
 */
export const CHART_COLORS = {
  // Performance colors
  excellent: '#10b981', // green
  good: '#3b82f6', // blue
  average: '#f59e0b', // amber
  poor: '#f97316', // orange
  critical: '#ef4444', // red

  // Risk profile colors
  riskCareful: '#10b981', // green
  riskBalanced: '#3b82f6', // blue
  riskSeeking: '#f59e0b', // amber

  // Priority colors
  priorityHigh: '#ef4444', // red
  priorityMedium: '#f59e0b', // amber
  priorityLow: '#10b981', // green

  // Rank colors
  gold: '#FFD700',
  silver: '#C0C0C0',
  bronze: '#CD7F32',

  // Chart axis colors
  axisText: '#9CA3AF', // gray-400
  backgroundLight: '#f8f9fa', // light gray
  borderLight: '#dee2e6', // border gray
  textDark: '#343a40', // dark text
  textMuted: '#6c757d', // muted text
  textSecondary: '#495057', // secondary text
  backgroundCard: '#ffffff' // white
} as const
